<template>
    <BModal
        :model-value="modal"
        centered
        @update:modelValue="updateModal"
        no-close-on-backdrop
        no-close-on-esc
    >
        <template #header>
            <div
                class="d-flex align-items-center justify-content-between modal-title w-100"
            >
                <h5 class="">Hotel Cost</h5>
                <BButton
                    variant="link"
                    class="close"
                    @click="updateModal(false)"
                >
                    <i class="ion ion-android-close"></i>
                </BButton>
            </div>
        </template>

        <BForm @submit.stop.prevent>
            <BRow>
                <BCol md="6">
                    <BFormGroup
                        label="PO Number *"
                        label-for="purchase_order_no"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.purchase_order_no"
                            type="text"
                            id="purchase_order_no"
                            placeholder="PO-XXXX"
                            :class="{
                                'is-invalid':
                                    v$.form.purchase_order_no.$dirty &&
                                    v$.form.purchase_order_no.$invalid,
                            }"
                        />
                        <BFormInvalidFeedback
                            :state="
                                v$.form.purchase_order_no.$dirty &&
                                v$.form.purchase_order_no.$invalid
                            "
                        >
                            {{ v$.form.purchase_order_no.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
                <BCol md="6">
                    <BFormGroup
                        label="Date *"
                        label-for="date"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            type="date"
                            v-model="form.date"
                            id="date"
                            :class="{
                                'is-invalid':
                                    v$.form.date.$dirty && v$.form.date.$invalid,
                            }"
                        />
                        <BFormInvalidFeedback
                            :state="v$.form.date.$dirty && v$.form.date.$invalid"
                        >
                            {{ v$.form.date.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
            </BRow>
            <BRow>
                <BCol>
                    <BFormGroup
                        label="Hotel Name *"
                        label-for="hotel_name"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.hotel_name"
                            type="text"
                            id="hotel_name"
                            placeholder="Hotel Name"
                            :class="{
                                'is-invalid':
                                    v$.form.hotel_name.$dirty &&
                                    v$.form.hotel_name.$invalid,
                            }"
                        />
                        <BFormInvalidFeedback
                            :state="
                                v$.form.hotel_name.$dirty &&
                                v$.form.hotel_name.$invalid
                            "
                        >
                            {{ v$.form.hotel_name.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
            </BRow>
            <BRow>
                <BCol>
                    <BFormGroup
                        label="Amount *"
                        label-for="amount"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.amount"
                            type="number"
                            id="amount"
                            placeholder="0"
                            :class="{
                                'is-invalid':
                                    v$.form.amount.$dirty &&
                                    v$.form.amount.$invalid,
                            }"
                        />
                        <BFormInvalidFeedback
                            :state="
                                v$.form.amount.$dirty && v$.form.amount.$invalid
                            "
                        >
                            {{ v$.form.amount.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
            </BRow>
            <BRow>
                <BCol>
                    <BFormGroup
                        label="Description"
                        label-for="description"
                        class="text-start mb-3"
                    >
                        <BFormTextarea
                            v-model="form.description"
                            id="description"
                            placeholder="Optional note"
                            rows="3"
                        />
                    </BFormGroup>
                </BCol>
            </BRow>
        </BForm>

        <template #footer>
            <div class="d-flex justify-content-end">
                <BButton variant="primary" class="me-2" @click="handleAction"
                    >Save</BButton
                >
                <BButton variant="danger" @click="updateModal(false)"
                    >Cancel</BButton
                >
            </div>
        </template>
    </BModal>
</template>

<script setup>
import { defineProps, defineEmits, reactive, watch } from "vue";
import {
    BModal,
    BRow,
    BCol,
    BForm,
    BFormInput,
    BButton,
    BFormGroup,
    BFormInvalidFeedback,
    BFormTextarea,
} from "bootstrap-vue-next";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required, minValue } from "@vuelidate/validators";
import { useToast } from "vue-toastification";

const toast = useToast();

// Props
const props = defineProps({
    action: String,
    modal: Boolean,
    selectedData: Object,
});

// Emit
const emit = defineEmits(["update:modal", "addOrUpdate"]);

// Function to update modal visibility
const updateModal = (value) => {
    emit("update:modal", value); // Sync modal state with parent
};

const resetForm = () => {
    Object.assign(form, { ...formData });
    v$.value.$reset();
};

const formData = {
    purchase_order_no: "",
    hotel_name: "",
    date: "",
    amount: "",
    description: "",
};

const form = reactive({ ...formData });

const rules = {
    form: {
        purchase_order_no: {
            required: helpers.withMessage("PO Number is required", required),
        },
        hotel_name: {
            required: helpers.withMessage("Hotel Name is required", required),
        },
        date: {
            required: helpers.withMessage("Date is required", required),
        },
        amount: {
            required: helpers.withMessage("Amount is required", required),
            minValue: helpers.withMessage(
                "Amount must be at least 1",
                minValue(1)
            ),
        },
    },
};

const v$ = useVuelidate(rules, { form });

// Init data jika update
watch(
    () => props.selectedData,
    (newData) => {
        if (newData && Object.keys(newData).length > 0) {
            Object.assign(form, { ...newData });
        } else {
            Object.assign(form, { ...formData });
        }
        v$.value.$reset();
    },
    { immediate: true }
);

const handleAction = () => {
    props.selectedData && props.selectedData.index !== undefined ? update() : create();
};

const create = async () => {
    v$.value.$touch();
    if (!v$.value.$invalid) {
        try {
            const payload = { 
                ...form,
                ref_no: '',
                remaining_amount: 0,
                paid_amount: 0
            };
            delete payload.index;
            emit("addOrUpdate", payload);
            toast.success("Hotel cost added successfully");
            updateModal(false);
            resetForm();
        } catch (error) {
            toast.error("Failed to add hotel cost");
        }
    }
};

const update = async () => {
    v$.value.$touch();
    if (!v$.value.$invalid) {
        try {
            emit("addOrUpdate", {
                index: props.selectedData.index,
                ...form,
                // Keep existing additional fields if they were in selectedData, assuming parent handles merge or we send what we have
            });
            toast.success("Hotel cost updated successfully");
            updateModal(false);
            resetForm();
        } catch (error) {
            toast.error("Failed to update hotel cost");
        }
    }
};
</script>
